// React core
import { useState, useCallback, useRef, useEffect, useMemo } from "react";

// External libraries
import { m, AnimatePresence } from "framer-motion";
import { Search, MessageSquare, Clock } from "lucide-react";

// Hooks
import { useRouter, useSearchParams } from "@/hooks/useRouter";
import { useProjects } from "@/hooks/useSearch";
import { useFind } from "@/hooks/useFind";
import { useSearchChatMode } from "@/hooks/useSearchChatMode";
import { useFilters } from "@/hooks/useFilters";
import { useSearchChatCache } from "@/hooks/useSearchChatCache";
import {
  useConnectedTools,
  type ConnectedTool,
} from "@/hooks/useConnectedTools";

// Local hooks
import { useCompactExpansion } from "./hooks/useCompactExpansion";
import { useCompactMentions } from "./hooks/useCompactMentions";

// Local components
import { CompactControls } from "./components/CompactControls";
import { CompactExpandedView } from "./components/CompactExpandedView";

// Shared components
import MentionMenu from "../../chat/mentions/MentionMenu";
import { MultiSelectProjectDropdown } from "../filters/MultiSelectProjectDropdown";
import TimeFilterButtons from "../filters/TimeFilterButtons";

// Types
import type { RAGContext } from "@/hooks/useRAG";
import type { Project } from "@/types/dashboard";

// Constants
import { getTimeFilterDisplay } from "@/lib/constants";
import { MultiSelectAgentDropdown } from "../filters/MultiSelectAgentDropdown";

const BLUR_DELAY = 200; // Delay before processing blur to allow button clicks (ms)

interface CompactUnifiedSearchProps {
  placeholder?: string;
}

/**
 * CompactUnifiedSearch - A unified search/chat input for the header
 *
 * Design principles:
 * 1. Single textarea element that never gets recreated
 * 2. Visual changes through CSS only (no conditional rendering of textarea)
 * 3. Expansion happens by changing container, not recreating elements
 * 4. All state changes preserve cursor position and focus
 * 5. Performance optimized with useMemo and useLayoutEffect where needed
 */
export function CompactUnifiedSearch({
  placeholder,
}: CompactUnifiedSearchProps) {
  const router = useRouter();
  const { setFindQuery } = useFind();

  // Mode and UI state
  const { mode, setMode } = useSearchChatMode();
  const query = useSearchParams().get("q") || "";

  const [value, _setValue] = useState(query);
  const [isFocused, setIsFocused] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [textareaHeight, setTextareaHeight] = useState(0);

  // Context for filters
  const [context, setContext] = useState<RAGContext>({
    projectIds: [],
    branchNames: [],
    timeFilter: null,
    agentNames: [],
  });

  // Data fetching
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { connectedTools: agents = [] } = useConnectedTools();
  const {
    projectIds: filterProjectIds,
    timeFilter: filterTimeFilter,
    agentNames: filterAgentNames,
  } = useFilters();

  // Calculate if we have context
  const hasContext =
    (context.projectIds?.length || 0) > 0 ||
    (context.branchNames?.length || 0) > 0 ||
    !!context.timeFilter;

  // Cache hook - handles only cache changes, not initialization (we don't want the cache to be used in the app header)
  const { clearCache, saveToCache } = useSearchChatCache();

  // Store the query in the cache,
  // no need to store the filters changes to the cache
  // since they trigger a search right away
  const setValue = useCallback(
    (value: string) => {
      _setValue(value);
      saveToCache({ query: value });
    },
    [saveToCache]
  );

  // Use expansion hook
  const {
    shouldExpand,
    collapsedWithContent,
    setShouldExpand,
    setCollapsedWithContent,
    hasMultiLineContent,
  } = useCompactExpansion({
    value,
    mode,
    isFocused,
    hasContext,
    textareaRef,
  });

  // Use mentions hook
  const {
    mentionContext,
    setMentionContext,
    handleTextChange,
    handleMentionSelect,
    handleAtButtonClick,
  } = useCompactMentions({
    value,
    setValue,
    mode,
    context,
    setContext,
    textareaRef,
  });

  // Sync filter context in search mode
  useEffect(() => {
    if (mode === "search") {
      setContext((prev) => ({
        ...prev,
        projectIds: filterProjectIds || [],
        timeFilter:
          filterTimeFilter === ""
            ? null
            : (filterTimeFilter as "today" | "week" | "month"),
        agentNames: filterAgentNames || [],
      }));
    }
  }, [filterProjectIds, filterTimeFilter, filterAgentNames, mode]);

  // Track textarea height for mention menu positioning
  useEffect(() => {
    if (!textareaRef.current) return;

    const updateHeight = () => {
      if (textareaRef.current) {
        setTextareaHeight(textareaRef.current.offsetHeight);
      }
    };

    // Update on resize
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(textareaRef.current);

    // Initial update
    updateHeight();

    return () => resizeObserver.disconnect();
  }, [textareaRef]);

  // Computed values - memoized for performance
  const selectedProjects = useMemo(
    () => new Set(context.projectIds || []),
    [context.projectIds]
  );
  const selectedBranches = useMemo(
    () => new Set(context.branchNames || []),
    [context.branchNames]
  );
  const hasFilters =
    selectedProjects.size > 0 ||
    !!context.timeFilter ||
    !!(context.agentNames && context.agentNames.length > 0);
  const filterCount =
    (selectedProjects.size > 0 ? 1 : 0) +
    (context.agentNames && context.agentNames.length > 0 ? 1 : 0) +
    (context.timeFilter ? 1 : 0);

  // Calculate session count - memoized to avoid expensive recalculation
  const sessionCount = useMemo(() => {
    if (!projects || projects.length === 0) return 0;
    return projects.reduce((sum, project) => {
      if (selectedProjects.size === 0 || selectedProjects.has(project.id)) {
        return sum + (project.sessionCount || 0);
      }
      return sum;
    }, 0);
  }, [projects, selectedProjects]);

  // Handle submit
  const handleSubmit = useCallback(() => {
    if (!value.trim()) return;

    if (mode === "search") {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set("q", value);
      if (context.projectIds?.length) {
        newSearchParams.set("p", context.projectIds.join(","));
      }
      if (context.timeFilter) {
        newSearchParams.set("t", context.timeFilter);
      }
      if (context.agentNames?.length) {
        newSearchParams.set("a", context.agentNames.join(","));
      }
      clearCache();
      router.push(`/search?${newSearchParams.toString()}`);
      setFindQuery(value);
    } else {
      // Chat mode - always navigate to "new" for a fresh chat
      if (typeof window !== "undefined") {
        sessionStorage.setItem("pendingChatMessage", value);
        sessionStorage.setItem("pendingChatContext", JSON.stringify(context));
      }

      // Check if we're already on a chat page
      const currentPath = window.location.pathname;
      if (currentPath.includes("/chats/")) {
        // Force a page reload to ensure the chat resets properly
        window.location.href = "/chats/new";
      } else {
        // Normal navigation for non-chat pages
        router.push("/chats/new");
      }

      setValue("");
      setContext({
        projectIds: [],
        branchNames: [],
        timeFilter: null,
        agentNames: [],
      });
      clearCache();
    }
  }, [
    value,
    mode,
    context,
    router,
    setFindQuery,
    clearCache,
    setValue,
    setContext,
  ]);

  // Handle keyboard
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (mentionContext.showMenu) return;

      if (e.key === "Enter") {
        if (mode === "search" || !e.shiftKey) {
          e.preventDefault();
          handleSubmit();
        }
      } else if (e.key === "Escape") {
        if (shouldExpand && mode === "chat") {
          e.preventDefault();
          textareaRef.current?.blur();
          if (hasMultiLineContent || hasContext) {
            setCollapsedWithContent(true);
            setShouldExpand(false);
          }
        }
      }
    },
    [
      mode,
      mentionContext.showMenu,
      handleSubmit,
      shouldExpand,
      hasMultiLineContent,
      hasContext,
      setCollapsedWithContent,
      setShouldExpand,
    ]
  );

  // Handle mode change
  const handleModeChange = useCallback(
    (newMode: "search" | "chat") => {
      // when switching from chat to search clear the query since it could be a multi-lines message
      // that won't be visible in the search field
      if (mode === "chat" && newMode === "search") {
        setValue("");
      }
      setMode(newMode);
      if (mentionContext.showMenu) {
        setMentionContext((prev) => ({ ...prev, showMenu: false }));
      }
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    },
    [mentionContext.showMenu, setMode, setMentionContext, setValue, mode]
  );

  // Focus/blur handling
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    requestAnimationFrame(() => {
      if (textareaRef.current && value) {
        const length = value.length;
        textareaRef.current.setSelectionRange(length, length);
      }
    });
  }, [value]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (containerRef.current?.contains(relatedTarget)) {
      return;
    }
    setTimeout(() => {
      setIsFocused(false);
    }, BLUR_DELAY);
  }, []);

  // Handle removing context items
  const handleRemoveContextItem = useCallback(
    (type: "project" | "branch" | "time", value: string) => {
      if (type === "project") {
        setContext((prev) => ({
          ...prev,
          projectIds: prev.projectIds?.filter((id) => id !== value) || [],
        }));
      } else if (type === "branch") {
        setContext((prev) => ({
          ...prev,
          branchNames: prev.branchNames?.filter((name) => name !== value) || [],
        }));
      } else if (type === "time") {
        setContext((prev) => ({
          ...prev,
          timeFilter: null,
        }));
      }
    },
    [setContext]
  );

  const handleClearContext = useCallback(() => {
    setContext({
      projectIds: [],
      branchNames: [],
      timeFilter: null,
      agentNames: [],
    });
  }, [setContext]);

  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    return mode === "search"
      ? "Search sessions..."
      : "Start a new chat across all your AI context...";
  };

  // Container classes
  const getContainerClasses = () => {
    const baseClasses = "bg-card border transition-all duration-200";

    if (shouldExpand && mode === "chat") {
      return `${baseClasses} rounded-2xl border-[#01B9D9]/60 shadow-xl absolute left-0 right-0 top-0 z-[100] p-3`;
    }

    if (mode === "chat") {
      const chatClasses =
        "rounded-xl border-[#01B9D9]/50 hover:border-[#01B9D9]/70 shadow-[0_0_20px_rgba(1,185,217,0.15)] hover:shadow-[0_0_30px_rgba(1,185,217,0.2)] relative";
      return isFocused && !shouldExpand
        ? `${baseClasses} ${chatClasses} border-[#01B9D9]/60 shadow-[0_0_30px_rgba(1,185,217,0.2)]`
        : `${baseClasses} ${chatClasses}`;
    }

    const searchClasses =
      "rounded-xl border-border shadow-sm hover:border-border hover:shadow-md relative";
    return isFocused && !shouldExpand
      ? `${baseClasses} ${searchClasses} border-border shadow-md`
      : `${baseClasses} ${searchClasses}`;
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Invisible spacer to maintain layout when expanded */}
      {shouldExpand && mode === "chat" && (
        <div
          className="rounded-xl border border-transparent"
          style={{ height: "48px" }}
        />
      )}

      {/* Main container */}
      <div className={getContainerClasses()}>
        <div
          className={shouldExpand ? "flex flex-col gap-2" : "flex items-center"}
        >
          {/* Input row */}
          <div
            className={
              shouldExpand ? "flex items-start" : "flex items-center flex-1 min-w-0"
            }
          >
            {/* Icon - only show in compact mode */}
            {!shouldExpand && (
              <div className="pl-3 pr-1 flex-shrink-0">
                {mode === "search" ? (
                  <Search className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <MessageSquare className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
            )}

            {/* Textarea */}
            <textarea
              ref={textareaRef}
              value={value}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={getPlaceholder()}
              className={`
                bg-transparent focus:outline-none resize-none text-base
                scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent
                ${shouldExpand ? "w-full" : "flex-1 min-w-0 px-2 py-2.5"}
              `}
              style={{
                lineHeight: "20px",
                ...(collapsedWithContent && !shouldExpand
                  ? {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }
                  : {}),
              }}
              rows={1}
            />

            {/* Controls */}
            <CompactControls
              mode={mode}
              value={value}
              shouldExpand={shouldExpand}
              hasFilters={hasFilters}
              filterCount={filterCount}
              onModeChange={handleModeChange}
              onSubmit={handleSubmit}
              onClear={() => setValue("")}
              onToggleFilters={() => setShowFilters(!showFilters)}
              onFocusTextarea={() => textareaRef.current?.focus()}
            />
          </div>

          {/* Expanded mode controls */}
          {shouldExpand && mode === "chat" && (
            <CompactExpandedView
              context={context}
              mentionContext={mentionContext}
              projects={projects}
              agents={agents}
              selectedProjects={selectedProjects}
              selectedBranches={selectedBranches}
              sessionCount={sessionCount}
              projectsLoading={projectsLoading}
              hasContext={hasContext}
              onAtButtonClick={handleAtButtonClick}
              onMentionSelect={handleMentionSelect}
              onMentionClose={() =>
                setMentionContext((prev) => ({ ...prev, showMenu: false }))
              }
              onRemoveContextItem={handleRemoveContextItem}
              onClearContext={handleClearContext}
              onUpdateTimeFilter={(filter) =>
                setContext((prev) => ({ ...prev, timeFilter: filter }))
              }
            />
          )}
        </div>
      </div>

      {/* Filters dropdown for search mode */}
      <AnimatePresence>
        {showFilters && mode === "search" && (
          <m.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 mt-2 p-3 bg-popover rounded-lg border border-border shadow-lg z-50"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Filters:</span>
              <MultiSelectProjectDropdown
                projects={projects as Project[]}
                triggerClassName="px-2 py-1 rounded-full flex items-center gap-1.5 text-xs font-medium transition-all"
              />
              <MultiSelectAgentDropdown
                agents={agents as ConnectedTool[]}
                triggerClassName="px-2 py-1 rounded-full flex items-center gap-1.5 text-xs font-medium transition-all"
              />
              <TimeFilterDropdown />
              {hasFilters && (
                <button
                  onClick={handleClearContext}
                  className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>

      {/* Mention menu when triggered by typing @ */}
      {mentionContext.showMenu &&
        !mentionContext.triggeredByButton &&
        mode === "chat" &&
        !projectsLoading && (
          <div
            className="absolute left-0 z-[150]"
            style={{
              top: shouldExpand
                ? `${textareaHeight + 16}px`
                : "calc(100% + 8px)",
            }}
          >
            <MentionMenu
              searchQuery={mentionContext.searchQuery}
              onSelect={handleMentionSelect}
              onClose={() =>
                setMentionContext((prev) => ({ ...prev, showMenu: false }))
              }
              projects={projects}
              agents={agents}
              recentBranches={Array.from(selectedBranches)}
              selectedProjects={selectedProjects}
              selectedBranches={selectedBranches}
              selectedTimeFilter={context.timeFilter}
              selectedAgents={context.agentNames || []}
              isWrapped={true}
            />
          </div>
        )}
    </div>
  );
}

// Time filter dropdown component
function TimeFilterDropdown() {
  const [showMenu, setShowMenu] = useState(false);
  const { timeFilter } = useFilters();

  return (
    <div className="relative">
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setShowMenu(!showMenu)}
        className={`
          px-2 py-1 rounded-full flex items-center gap-1.5 text-xs font-medium
          transition-all duration-200
          ${
            timeFilter !== ""
              ? "border border-border text-foreground hover:border-border hover:bg-muted"
              : "text-muted-foreground hover:bg-muted"
          }
        `}
      >
        <Clock className="w-3 h-3" />
        <span>{getTimeFilterDisplay(timeFilter || "")}</span>
      </button>

      {showMenu && (
        <TimeFilterButtons
          setShowTimeMenu={setShowMenu}
          className="absolute left-0 top-full bg-popover rounded-lg border border-border shadow-lg py-1 z-50 min-w-[120px] mt-1"
        />
      )}
    </div>
  );
}
